import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import businessService from "./businessService";
import toast from "react-hot-toast";
import { RootState } from "../index";
import { Business } from "../../types/business";

const initialState: Business = {
    businessInfo: {},
    categories: {},
    mediaInfo: {},
    workingHours: {},
    currentStep: 1,
    businesses: [],
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: "",
};

// Async Thunks

// Fetch all businesses
export const fetchBusinesses = createAsyncThunk<
    any,
    void,
    { state: RootState }
>("business/fetchBusinesses", async (_, thunkAPI) => {
    try {
        return await businessService.fetchBusinesses();
    } catch (error: any) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        toast.error(message);
        return thunkAPI.rejectWithValue(message);
    }
});


// Create a new business
export const createBusiness = createAsyncThunk<
    any,
    any,
    { state: RootState }
>("business/createBusiness", async (data, thunkAPI) => {
    const { businessInfo, categories, mediaInfo, workingHours } =
        thunkAPI.getState().business;
    try {
        const formData = {
            businessInfo,
            categories,
            mediaInfo,
            workingHours,
        };
        return await businessService.createBusiness(formData);
    } catch (error: any) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        toast.error(message);
        return thunkAPI.rejectWithValue(message);
    }
});

export const deleteBusiness = createAsyncThunk<
    any,
    string, // The ID of the business to delete
    { state: RootState }
>("business/deleteBusiness", async (businessId, thunkAPI) => {
    try {
        await businessService.deleteBusiness(businessId);
        toast.success("Business deleted successfully!");
        // Refetch businesses after deletion
        thunkAPI.dispatch(fetchBusinesses());
        return businessId; // Return the deleted business ID
    } catch (error: any) {
        const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        toast.error(message);
        return thunkAPI.rejectWithValue(message);
    }
});

// Slice
const businessSlice = createSlice({
    name: "business",
    initialState,
    reducers: {
        resetState: (state) => {
            state.businessInfo = {};
            state.categories = {};
            state.mediaInfo = {};
            state.workingHours = {};
            state.currentStep = 1;
            state.isError = false;
            state.isSuccess = false;
            state.isLoading = false;
            state.message = "";
        },
        setBusinessInfo: (state, action) => {
            state.businessInfo = action.payload;
            state.currentStep = 2;
        },
        setCategories: (state, action) => {
            state.categories = action.payload;
            state.currentStep = 3;
        },
        setMediaInfo: (state, action) => {
            state.mediaInfo = action.payload;
            state.currentStep = 4;
        },
        setWorkingHours: (state, action) => {
            state.workingHours = action.payload;
            state.currentStep = 5;
        },
        setCurrentStep: (state, action) => {
            state.currentStep = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBusinesses.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchBusinesses.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.businesses = payload;
            })
            .addCase(fetchBusinesses.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(createBusiness.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createBusiness.fulfilled, (state) => {
                state.isLoading = false;
                state.isSuccess = true;
                toast.success("Business created successfully!");
            })
            .addCase(createBusiness.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            });
    },
});

// Actions
export const {
    resetState,
    setBusinessInfo,
    setCategories,
    setMediaInfo,
    setWorkingHours,
    setCurrentStep,
} = businessSlice.actions;

// Reducer
export default businessSlice.reducer;
